import React, { useState, useEffect } from 'react';
import { dashboardAPI, performanceAPI } from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { 
  TrendingUp, 
  TrendingDown,
  BarChart3,
  Calendar,
  Target,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MONTHS } from '../lib/utils';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  BarChart, Bar, AreaChart, Area, ComposedChart, Legend, ReferenceLine
} from 'recharts';
import { motion } from 'framer-motion';

const Analytics = () => {
  const { isDark } = useTheme();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [trends, setTrends] = useState(null);
  const [yearOverYear, setYearOverYear] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch trends for selected year and previous year
        const [currentTrends, prevTrends] = await Promise.all([
          dashboardAPI.getTrends(selectedYear),
          dashboardAPI.getTrends(selectedYear - 1)
        ]);
        
        setTrends(currentTrends.data);
        
        // Create YoY comparison data
        const yoy = currentTrends.data.monthly_trends.map((curr, i) => ({
          name: MONTHS[curr.month - 1]?.substring(0, 3),
          current: curr.avg_health,
          previous: prevTrends.data.monthly_trends[i]?.avg_health || 0,
          change: curr.avg_health - (prevTrends.data.monthly_trends[i]?.avg_health || 0)
        }));
        setYearOverYear(yoy);
        
        // Generate forecast (simple linear regression)
        const validScores = currentTrends.data.monthly_trends.filter(m => m.avg_health > 0);
        if (validScores.length >= 3) {
          const lastThree = validScores.slice(-3);
          const avgChange = (lastThree[2]?.avg_health - lastThree[0]?.avg_health) / 2;
          const lastScore = lastThree[lastThree.length - 1]?.avg_health || 3.5;
          
          const forecastData = currentTrends.data.monthly_trends.map((m, i) => ({
            name: MONTHS[m.month - 1]?.substring(0, 3),
            actual: m.avg_health || null,
            forecast: i >= validScores.length ? Math.min(5, Math.max(1, lastScore + avgChange * (i - validScores.length + 1))) : null
          }));
          setForecast(forecastData);
        }
        
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedYear]);

  // Calculate stats
  const currentAvg = trends?.monthly_trends?.filter(m => m.avg_health > 0).slice(-1)[0]?.avg_health || 0;
  const prevAvg = trends?.monthly_trends?.filter(m => m.avg_health > 0).slice(-2, -1)[0]?.avg_health || 0;
  const monthChange = currentAvg - prevAvg;
  const yoyChange = yearOverYear.length > 0 ? yearOverYear.filter(y => y.current > 0).slice(-1)[0]?.change || 0 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white font-outfit">
            Advanced Analytics
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Deep insights and forecasting
          </p>
        </div>
        <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-32" data-testid="analytics-year-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026].map((year) => (
              <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="dark:bg-slate-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Current Score</p>
                  <p className="text-2xl font-bold text-slate-800 dark:text-white mt-1">{currentAvg.toFixed(2)}</p>
                </div>
                <div className="p-3 bg-[#331062]/10 dark:bg-[#331062]/30 rounded-xl">
                  <Activity className="w-6 h-6 text-[#331062] dark:text-[#CCFF00]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="dark:bg-slate-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Month-over-Month</p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className={`text-2xl font-bold ${monthChange >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {monthChange >= 0 ? '+' : ''}{monthChange.toFixed(2)}
                    </p>
                    {monthChange >= 0 ? (
                      <ArrowUpRight className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <ArrowDownRight className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                </div>
                <div className={`p-3 rounded-xl ${monthChange >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                  <TrendingUp className={`w-6 h-6 ${monthChange >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="dark:bg-slate-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Year-over-Year</p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className={`text-2xl font-bold ${yoyChange >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {yoyChange >= 0 ? '+' : ''}{yoyChange.toFixed(2)}
                    </p>
                    {yoyChange >= 0 ? (
                      <ArrowUpRight className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <ArrowDownRight className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                </div>
                <div className={`p-3 rounded-xl ${yoyChange >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                  <Calendar className="w-6 h-6 text-[#331062] dark:text-[#CCFF00]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="bg-gradient-to-br from-[#331062] to-[#180630] text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/70">Forecast Next Month</p>
                  <p className="text-2xl font-bold text-white mt-1">
                    {forecast.filter(f => f.forecast).slice(-1)[0]?.forecast?.toFixed(2) || 'N/A'}
                  </p>
                </div>
                <div className="p-3 bg-white/10 rounded-xl">
                  <Zap className="w-6 h-6 text-[#CCFF00]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Year-over-Year Comparison */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Card className="dark:bg-slate-800">
            <CardHeader>
              <CardTitle className="text-lg font-outfit flex items-center gap-2 dark:text-white">
                <Calendar className="w-5 h-5 text-[#331062] dark:text-[#CCFF00]" />
                Year-over-Year Comparison
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={yearOverYear}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#E2E8F0'} />
                    <XAxis dataKey="name" stroke={isDark ? '#9CA3AF' : '#64748B'} fontSize={12} />
                    <YAxis domain={[0, 5]} stroke={isDark ? '#9CA3AF' : '#64748B'} fontSize={12} />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: isDark ? '#1F2937' : 'white',
                        border: `1px solid ${isDark ? '#374151' : '#E2E8F0'}`,
                        borderRadius: '8px',
                      }}
                    />
                    <Legend />
                    <Bar dataKey="current" name={`${selectedYear}`} fill="#331062" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="previous" name={`${selectedYear - 1}`} fill="#CCFF00" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Performance Forecast */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <Card className="dark:bg-slate-800">
            <CardHeader>
              <CardTitle className="text-lg font-outfit flex items-center gap-2 dark:text-white">
                <Zap className="w-5 h-5 text-[#331062] dark:text-[#CCFF00]" />
                Performance Forecast
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={forecast}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#E2E8F0'} />
                    <XAxis dataKey="name" stroke={isDark ? '#9CA3AF' : '#64748B'} fontSize={12} />
                    <YAxis domain={[0, 5]} stroke={isDark ? '#9CA3AF' : '#64748B'} fontSize={12} />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: isDark ? '#1F2937' : 'white',
                        border: `1px solid ${isDark ? '#374151' : '#E2E8F0'}`,
                        borderRadius: '8px',
                      }}
                    />
                    <Legend />
                    <ReferenceLine y={3.7} stroke="#F59E0B" strokeDasharray="5 5" label="Target" />
                    <Line type="monotone" dataKey="actual" stroke="#331062" strokeWidth={3} dot={{ fill: '#331062' }} name="Actual" connectNulls={false} />
                    <Line type="monotone" dataKey="forecast" stroke="#CCFF00" strokeWidth={3} strokeDasharray="5 5" dot={{ fill: '#CCFF00' }} name="Forecast" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Trend Analysis */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
        <Card className="dark:bg-slate-800">
          <CardHeader>
            <CardTitle className="text-lg font-outfit flex items-center gap-2 dark:text-white">
              <TrendingUp className="w-5 h-5 text-[#331062] dark:text-[#CCFF00]" />
              Monthly Trend Analysis with Scores Count
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trends?.monthly_trends?.map(m => ({
                  name: MONTHS[m.month - 1]?.substring(0, 3),
                  score: m.avg_health,
                  count: m.scores_count
                })) || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#E2E8F0'} />
                  <XAxis dataKey="name" stroke={isDark ? '#9CA3AF' : '#64748B'} fontSize={12} />
                  <YAxis yAxisId="left" domain={[0, 5]} stroke={isDark ? '#9CA3AF' : '#64748B'} fontSize={12} />
                  <YAxis yAxisId="right" orientation="right" stroke={isDark ? '#9CA3AF' : '#64748B'} fontSize={12} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: isDark ? '#1F2937' : 'white',
                      border: `1px solid ${isDark ? '#374151' : '#E2E8F0'}`,
                      borderRadius: '8px',
                    }}
                  />
                  <Legend />
                  <Area yAxisId="left" type="monotone" dataKey="score" fill="#331062" fillOpacity={0.2} stroke="#331062" strokeWidth={2} name="Avg Score" />
                  <Bar yAxisId="right" dataKey="count" fill="#CCFF00" opacity={0.8} name="Scores Entered" radius={[4, 4, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Analytics;
